import React from 'react';
import { Button, Col, DatePicker, Form, Input, Popconfirm, Row, Select, Space, Switch, Upload } from 'antd';
import { openNotification } from '../../../utils';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { apiFetch } from 'services/apiClient';
import { listMeasuringInstrumentsAll } from '../../measuring-instruments/services/measuring-instrumentService';
import { createMeasuringInstrumentInventory, updateMeasuringInstrumentInventory, getMeasuringInstrumentInventory } from '../services/measuring-instrument-inventoryService';
import { CreateMeasuringInstrumentInventoryDto } from '../types/MeasuringInstrumentInventory';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

export default function MeasuringInstrumentInventoryForm({
  id,
  onSuccess,
  referenceId,
  stack = false,
  reloadAfterSave = false,
}: {
  id?: number;
  onSuccess: () => void;
  referenceId?: number;
  stack?: boolean;
  reloadAfterSave?: boolean;
}) {
  const [form] = Form.useForm<CreateMeasuringInstrumentInventoryDto>();
  const [measuringInstrumentOptions, setMeasuringInstrumentOptions] = React.useState<any[]>([]);
  const [accountOptions, setAccountOptions] = React.useState<any[]>([]);
  const [invoiceRemoving, setInvoiceRemoving] = React.useState(false);
  const [existingInvoice, setExistingInvoice] = React.useState<UploadFile | null>(null);
  const validateInvoiceFile = (_: any, value: UploadFile[] | undefined) => {
    if (id) {
      return Promise.resolve();
    }
    if (Array.isArray(value) && value.length) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Fatura yüklenmesi zorunludur'));
  };

  const buildPayloadFromFormValues = React.useCallback(async (
    values: CreateMeasuringInstrumentInventoryDto,
    opts?: { forceInvoiceNull?: boolean },
  ) => {
    const data: any = { ...values };
    if (data.calibrationDate) data.calibrationDate = data.calibrationDate.format('YYYY-MM-DD');
    if (data.nextCalibrationDate) data.nextCalibrationDate = data.nextCalibrationDate.format('YYYY-MM-DD');
    if (data.purchaseDate) data.purchaseDate = data.purchaseDate.format('YYYY-MM-DD');
    if (data.warrantyValidUntil) data.warrantyValidUntil = data.warrantyValidUntil.format('YYYY-MM-DD');
    if (data.account) data.account = { id: data.account };

    if (opts?.forceInvoiceNull) {
      data.invoiceFilePath = null;
      data.clearInvoiceFilePath = true;
      return data;
    }

    if (Array.isArray(data.invoiceFilePath)) {
      const [file] = data.invoiceFilePath as UploadFile[];
      if (file?.originFileObj) {
        const fd = new FormData();
        fd.append('code', 'invoiceFilePath');
        fd.append('document', file.originFileObj);
        const uploaded = await apiFetch('/documents', { method: 'POST', body: fd });
        data.invoiceFilePath = uploaded.documentUrl || uploaded.filePath || uploaded;
      } else if (file?.url) {
        data.invoiceFilePath = file.url;
      } else {
        data.invoiceFilePath = null;
      }
    } else if (data.invoiceFilePath === undefined) {
      delete data.invoiceFilePath;
    }

    return data;
  }, []);

  const handleRemoveInvoice = async () => {
    if (!id) return;
    setInvoiceRemoving(true);
    try {
      const formValues = form.getFieldsValue(true) as CreateMeasuringInstrumentInventoryDto;
      const data = await buildPayloadFromFormValues(formValues, { forceInvoiceNull: true });
      await updateMeasuringInstrumentInventory(id, data);
      form.setFieldsValue({ invoiceFilePath: [] });
      setExistingInvoice(null);
      openNotification('success', 'Başarılı', 'Fatura silindi.');
    } catch {
      openNotification('error', 'Hata', 'Fatura silinemedi.');
    } finally {
      setInvoiceRemoving(false);
    }
  };

  const loadInventoryDetail = React.useCallback(async () => {
    if (!id) {
      form.resetFields();
      form.setFieldsValue({ isActive: true, invoiceFilePath: [] });
      setExistingInvoice(null);
      return;
    }
    try {
      const d = await getMeasuringInstrumentInventory(id);
      const data: any = { ...d };
      if (data.calibrationDate) data.calibrationDate = dayjs(data.calibrationDate);
      if (data.nextCalibrationDate) data.nextCalibrationDate = dayjs(data.nextCalibrationDate);
      if (data.purchaseDate) data.purchaseDate = dayjs(data.purchaseDate);
      if (data.warrantyValidUntil) data.warrantyValidUntil = dayjs(data.warrantyValidUntil);
      if (data.measuringInstrument) data.measuringInstrument = typeof data.measuringInstrument === 'object' ? data.measuringInstrument.id : data.measuringInstrument;
      if (data.account) data.account = typeof data.account === 'object' ? data.account.id : data.account;
      if (data.invoiceFilePath) {
        const url = data.invoiceFilePath;
        const name = url.split('/').pop() || 'invoice';
        const invoiceFile = {
          uid: '-invoice',
          name,
          status: 'done',
          url
        } as UploadFile;
        data.invoiceFilePath = [invoiceFile] as UploadFile[];
        setExistingInvoice(invoiceFile);
      } else {
        setExistingInvoice(null);
      }
      form.setFieldsValue(data);
    } catch {
      openNotification('error', 'Hata', 'Ölçüm Cihazı Envanteri bilgileri alınamadı');
    }
  }, [id, form]);

  React.useEffect(() => {
    loadInventoryDetail();
  }, [loadInventoryDetail]);

  React.useEffect(() => {
    listMeasuringInstrumentsAll().then((list: any[]) => {
      const activeOnly = Array.isArray(list) ? list.filter((o: any) => o?.isActive) : [];
      const current = form.getFieldValue('measuringInstrument');
      let options = activeOnly;
      if (current && !activeOnly.some((o: any) => o?.id === current)) {
        const found = Array.isArray(list) ? list.find((o: any) => o?.id === current) : undefined;
        if (found) options = [found, ...activeOnly];
      }
      setMeasuringInstrumentOptions(options);
    });
    apiFetch('/accounts').then(setAccountOptions);
  }, [form]);

  const onFinish = async (values: CreateMeasuringInstrumentInventoryDto) => {
    try {
      const data = await buildPayloadFromFormValues(values);
      if (id) {
        await updateMeasuringInstrumentInventory(id, data);
        openNotification('success', 'Başarılı', 'Ölçüm Cihazı Envanteri Güncellendi');
        if (reloadAfterSave) {
          await loadInventoryDetail();
          return;
        }
      } else {
        await createMeasuringInstrumentInventory(data);
        openNotification('success', 'Başarılı', 'Ölçüm Cihazı Envanteri Eklendi');
      }
      onSuccess();
    } catch (e) {
      openNotification('error', 'Hata', 'Ölçüm Cihazı Envanteri işlemi başarısız');
    }
  };
  return (
    <>

      <Form form={form} onFinish={onFinish} layout='vertical' requiredMark className={stack ? 'form-compact' : undefined} initialValues={{ isActive: true }}>
        { /* isEdit true when editing; some fields hidden on create and readonly on edit */}
        {(() => {
          const isEdit = !!id; return (<>
            <Row gutter={stack ? [8, 8] : [16, 16]}>
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item name='measuringInstrument' label='Ölçüm Cihazı' rules={[{ required: true }]}>
                  <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz'>
                    {measuringInstrumentOptions.map(o => (
                      <Select.Option key={o.id} value={o.id} disabled={o?.isActive === false}>
                        {o.name ?? o.id}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item name='serialNumber' label='Seri Numarası' rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item name='brand' label='Marka' rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item name='model' label='Model'>
                  <Input />
                </Form.Item>
              </Col>

              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item
                  name='purchaseOrganization'
                  label='Satın Alınan Kuruluş'
                  rules={[
                    { required: true, message: 'Satın alınan kuruluş zorunludur' },
                    { max: 255, message: 'En fazla 255 karakter girebilirsiniz' }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item
                  name='purchaseDate'
                  label='Satın Alınma Tarihi'
                  rules={[{ required: true, message: 'Satın alınma tarihi zorunludur' }]}
                >
                  <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item
                  name='warrantyValidUntil'
                  label='Garanti Geçerlilik Tarihi'
                  rules={[{ required: true, message: 'Garanti geçerlilik tarihi zorunludur' }]}
                >
                  <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                {id && existingInvoice ? (
                  <Form.Item label='Fatura' required>
                    <Space direction='vertical'>
                      <Button
                        type='link'
                        href={existingInvoice.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ padding: 0, height: 'auto' }}
                      >
                        Fatura Göster
                      </Button>
                      <Popconfirm
                        placement='left'
                        title='Faturayı silmek istediğinize emin misiniz?'
                        onConfirm={handleRemoveInvoice}
                        okText='Evet'
                        cancelText='Hayır'
                      >
                        <Button danger loading={invoiceRemoving} disabled={invoiceRemoving}>Sil</Button>
                      </Popconfirm>
                    </Space>
                  </Form.Item>
                ) : (
                  <Form.Item
                    name='invoiceFilePath'
                    label='Fatura'
                    required
                    valuePropName='fileList'
                    getValueFromEvent={e => (Array.isArray(e?.fileList) ? e.fileList.slice(-1) : e?.fileList)}
                    rules={[
                      { required: true, message: 'Fatura zorunludur' },
                      { validator: validateInvoiceFile },
                    ]}
                  >
                    <Upload
                      className='mi-invoice-upload'
                      multiple={false}
                      maxCount={1}
                      beforeUpload={() => false}
                      accept='.pdf,.jpg,.jpeg,.png,.doc,.docx'
                      listType='text'
                    >
                      <Button icon={<InboxOutlined />}>Fatura Yükle</Button>
                    </Upload>
                  </Form.Item>
                )}
              </Col>
              {isEdit && (
                <>
                  <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                    <Form.Item name='calibrationDate' label='Kalibrasyon Tarihi'>
                      <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} disabled />
                    </Form.Item>
                  </Col>
                  <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                    <Form.Item name='nextCalibrationDate' label='Kalibrasyon Geçerlilik Tarihi'>
                      <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{ width: '100%' }} disabled />
                    </Form.Item>
                  </Col>
                  <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                    <Form.Item name='account' label='Kullanıcı'>
                      <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' disabled>
                        {accountOptions.map(o => {
                          const email = o?.email ?? '';
                          const fullName = [o?.firstName, o?.lastName].filter(Boolean).join(' ').trim();
                          const label = [email, fullName].filter(Boolean).join(' - ') || o?.id;
                          return (
                            <Select.Option key={o.id} value={o.id}>{label}</Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                </>
              )}
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 12, md: 8 })}>
                <Form.Item name='isActive' valuePropName='checked' label='Aktif/Pasif'>
                  <Switch checkedChildren='Aktif' unCheckedChildren='Pasif' />
                </Form.Item>
              </Col>
              <Col {...(stack ? { xs: 24, sm: 24, md: 24 } : { xs: 24, sm: 24, md: 24 })}>
                <Form.Item name='deviceFeatures' label='Cihaza Ait Özellikler'>
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Col>
            </Row>
            <Row justify={"end"}>
              <Button htmlType='submit' type='primary' className='btn-inline'>Kaydet</Button>
            </Row>
          </>);
        })()}
      </Form>
    </>
  );
}
